import styled, {css} from "styled-components";
import {fontFamilies} from "theme/tokens";

/** The shadow surrounds the masked paper, rather than being clipped by its own notch mask. */
export const TicketShadow = styled.div`
	filter: drop-shadow(0 2px 5px color-mix(in srgb, ${({theme}) => theme.tokens.ticketInk} 30%, transparent));
`;

/** Both forms retain a perforated edge; the dialog has a narrow paper margin in place of the stub. */
export const Ticket = styled.div<{$stub: boolean}>`
	--ticket-stub-width: ${({$stub}) => $stub ? "104px" : "16px"};
	--ticket-print-end: ${({$stub}) => $stub ? "12px" : "28px"};
	position: relative;
	display: grid;
	grid-template-columns: ${({$stub}) => ($stub ? "minmax(0, 1fr) var(--ticket-stub-width)" : "minmax(0, 1fr)")};
	min-width: 0;
	/* Inverted (owner 2026-09-09): the print is red paper with white ink; the stub is the pale half. */
	color: ${({theme}) => theme.tokens.darkButtonInk};
	/* Cut both printed halves at the same seam, not a separate strip between them. The seam itself
	   is a dashed CUT between the two notches (owner 2026-09-09: "see through it"), so the page shows
	   through the perforation rather than a painted line; a larger bite sits mid-height on the
	   print's left edge, beside the tuck control (owner, same round). The dialog's stubless copy
	   keeps both, and its paper margin carries the stub's bitten right edge (owner: the dialog
	   ticket was missing both), since that margin is where the stub was torn from. The layers below intersect:
	   each passes everything except its own hole, so the paper is what every layer keeps. The dash
	   layer is a 2px column, opaque 4px in every 8px, between the top and bottom 12px so the notches
	   keep their rims; the two solid layers either side of it keep the rest of the paper. */
	${({theme, $stub}) => css`
		mask:
			radial-gradient(circle at calc(100% - var(--ticket-stub-width)) 0, transparent 9px, ${theme.tokens.ticketInk} 9.5px),
			radial-gradient(circle at calc(100% - var(--ticket-stub-width)) 100%, transparent 9px, ${theme.tokens.ticketInk} 9.5px),
			radial-gradient(circle at 0 50%, transparent 13px, ${theme.tokens.ticketInk} 13.5px),
			${$stub ? "" : css`radial-gradient(circle at 100% 50%, transparent 3.5px, ${theme.tokens.ticketInk} 4px) right top / 100% 12px repeat-y,`}
			linear-gradient(${theme.tokens.ticketInk}, ${theme.tokens.ticketInk}) 0 0 / calc(100% - var(--ticket-stub-width) - 1px) 100% no-repeat,
			linear-gradient(${theme.tokens.ticketInk}, ${theme.tokens.ticketInk}) 100% 0 / calc(var(--ticket-stub-width) - 1px) 100% no-repeat,
			repeating-linear-gradient(${theme.tokens.ticketInk} 0 4px, transparent 4px 8px) calc(100% - var(--ticket-stub-width) + 1px) 12px / 2px calc(100% - 24px) no-repeat,
			linear-gradient(${theme.tokens.ticketInk}, ${theme.tokens.ticketInk}) 0 0 / 100% 12px no-repeat,
			linear-gradient(${theme.tokens.ticketInk}, ${theme.tokens.ticketInk}) 0 100% / 100% 12px no-repeat;
		mask-composite: intersect, intersect, intersect, ${$stub ? "" : "intersect, "}add, add, add, add, add;
	`}
`;

export const TicketMain = styled.div`
	position: relative;
	min-width: 0;
	/* Shallow print (owner 2026-09-09, "reduce the height"): the tuck control sets the eyebrow band's
	   height, so the rest of the print is what can give. */
	padding: 6px var(--ticket-print-end) 10px 12px;
	border-radius: 8px;
	background: ${({theme}) => theme.tokens.ticketRed};
	/* Centred print on every copy (owner 2026-09-09), the success card's included. */
	text-align: center;
	overflow-wrap: anywhere;
`;

export const TicketEyebrow = styled.p`
	margin: 0;
	min-height: 32px;
	/* Equal insets keep the eyebrow centred while the left one clears the tuck control. The slot's tag
	   body declares the clearance, because only there does a tuck control sit over the print: a ticket in
	   a dialog has the dialog's own ✕ instead, and its eyebrow keeps the whole width (ui#3343). */
	padding-inline: var(--ticket-tuck-clearance, 0px);
	display: flex; align-items: center; justify-content: center;
	font-family: ${fontFamilies.mono};
	font-size: 12px; font-weight: 800; line-height: 1.4; letter-spacing: .06em;
	text-transform: uppercase;
`;

/** The paper remains actionable after the stub tears, including when checkout was cancelled. */
export const TicketReopen = styled.button`
	position: absolute;
	inset: 0;
	width: 100%; height: 100%; min-height: 44px;
	border: 0; border-radius: inherit;
	background: transparent;
	cursor: pointer;
	&:focus-visible { outline: 2px solid currentColor; outline-offset: -4px; }
`;

export const TicketHeading = styled.h3`
	margin: 0;
	font: inherit;
`;

export const TicketValue = styled.span<{$compact: boolean}>`
	display: block;
	font-size: ${({$compact}) => $compact ? "38px" : "44px"};
	font-weight: 700; line-height: 1; letter-spacing: -.04em;
	@media (max-width: 480px) { font-size: ${({$compact}) => $compact ? "34px" : "40px"}; }
`;

export const TicketTerms = styled.span`
	display: block;
	margin-top: 2px;
	font-family: ${fontFamilies.mono};
	font-size: 10.5px; font-weight: 700; line-height: 1.4; letter-spacing: .08em;
	text-transform: uppercase;
`;

export const TicketMeta = styled.div`
	display: flex; flex-wrap: wrap; justify-content: space-between; gap: 2px 8px;
	margin-top: 6px; padding-top: 5px;
	border-top: 1.5px dashed ${({theme}) => theme.tokens.ticketLine};
	font-family: ${fontFamilies.mono};
	font-size: 9px; line-height: 1.5; letter-spacing: .03em;
	text-transform: uppercase;
	/* Full print white, not a tint: on the bright red a tint measured 3.75:1 at 9px (Axe gate). */
`;

/** Paints nothing: the seam is cut out of the ticket's mask. It remains as the seam's measured position. */
export const TicketPerforation = styled.div`
	position: absolute;
	right: var(--ticket-stub-width); top: 10px; bottom: 10px;
	border-left: 2px solid transparent;
	pointer-events: none;
`;

export const TicketStub = styled.a<{$torn: boolean}>`
	position: relative;
	display: grid; place-items: center; align-content: center; gap: 6px;
	min-width: 44px; min-height: 44px; padding: 8px 6px;
	border: 0; border-radius: 8px;
	background: ${({theme}) => theme.tokens.ticketPaper};
	color: ${({theme}) => theme.tokens.ticketRed};
	/* Perforated outer edge (owner 2026-09-09): a semicircular bite every 12px down the right edge,
	   the way the stub of a printed coupon is torn from its sheet. The wrapper's drop shadow follows
	   the cut. The token is mask ink here, not a colour, as in the notch mask above. */
	mask: radial-gradient(circle at 100% 50%, transparent 3.5px, ${({theme}) => theme.tokens.ticketInk} 4px) right top / 100% 12px repeat-y;
	font: inherit; text-decoration: none; text-align: center;
	text-transform: uppercase;
	cursor: pointer;
	transform-origin: 0 50%;
	transition: transform .5s cubic-bezier(.4,.1,.2,1), opacity .5s, visibility 0s;
	&:hover { background: ${({theme}) => theme.tokens.ticketPaperHover}; }
	&:focus-visible { outline: 2px solid currentColor; outline-offset: -5px; }
	${({$torn}) => $torn && css`
		transform: rotate(14deg) translate(26px, 10px);
		opacity: 0; visibility: hidden; pointer-events: none;
		transition: transform .5s cubic-bezier(.4,.1,.2,1), opacity .5s, visibility 0s .5s;
	`}
	@media (prefers-reduced-motion: reduce) { transition: none; transform: none; }
`;

export const TicketClaim = styled.span`
	font-family: ${fontFamilies.mono};
	font-size: 15px; font-weight: 800; line-height: 1.25; letter-spacing: .02em;
`;

export const TicketStubLabel = styled.span`
	font-family: ${fontFamilies.mono};
	font-size: 10.5px; font-weight: 700; line-height: 1.4; letter-spacing: .04em;
`;
